import { pageContainerStyle } from "@commonStyle";
import { useResponsive } from "@hooks";
import { Platform, StyleSheet } from "react-native";

// Palette issue de la maquette (design system Ligne 360)
export const couleursCET = {
    bleu: '#1F4E96',
    primaire: '#483CC9',
    primaireDesactive: '#C7D6F5',
    texteBouton: '#F1F1F1',
    grey800: '#2B2B30',
    grey600: '#5C5C63',
    grey450: '#8E8E93',
    grey400: '#A3A3A6',
    grey300: '#C7C7CA',
    grey200: '#E4E4E6',
    grey100: '#EFEFF0',
    grey50: '#F7F7F8',
    danger: '#D92D20',
    succes: '#12805C',
    orangeBg: '#FFF1E0',
    orangeFg: '#A35200',
    vertBg: '#E6F4EE',
    vertFg: '#12805C',
    metierTonique: '#F3ECF2',
    metierSecondaire: '#592156',
}

const ombre = (opacite: number, rayon: number, hauteur: number) => Platform.select({
    web: { boxShadow: `0 ${hauteur}px ${rayon}px rgba(15,23,42,${opacite})` },
    default: {
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: hauteur },
        shadowOpacity: opacite,
        shadowRadius: rayon,
        elevation: 2,
    },
})

export default function useGestionCETStyle() {
    const isResponsive = useResponsive();
    return {
        styles: StyleSheet.create({
            container: {
                ...pageContainerStyle,
                backgroundColor: '#ffffff',
                paddingHorizontal: isResponsive ? 12 : 24,
                paddingTop: isResponsive ? 16 : 28,
                paddingBottom: 40,
                gap: 24
            },
            // Carte état du compte
            etatCard: {
                backgroundColor: '#ffffff',
                borderWidth: 1,
                borderColor: couleursCET.grey400,
                borderRadius: 12,
                overflow: 'hidden',
                ...ombre(0.08, 3, 1),
            },
            etatHeader: {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
                paddingTop: 18,
                paddingHorizontal: 20,
            },
            title: {
                fontSize: 18,
                fontWeight: '600',
                color: couleursCET.grey800,
            },
            badgePlafond: {
                height: 24,
                paddingHorizontal: 10,
                borderRadius: 999,
                justifyContent: 'center',
                backgroundColor: couleursCET.metierTonique,
            },
            badgePlafondText: {
                fontSize: 12,
                fontWeight: '500',
                color: couleursCET.metierSecondaire,
            },
            mainInfo: {
                marginTop: 16,
                marginHorizontal: 20,
                paddingVertical: 18,
                paddingHorizontal: 16,
                borderRadius: 8,
                backgroundColor: couleursCET.grey50,
                alignItems: 'center',
            },
            primaryLabel: {
                fontSize: 13,
                fontWeight: '500',
                color: couleursCET.grey600,
            },
            primaryValue: {
                marginTop: 4,
                fontSize: isResponsive ? 28 : 34,
                fontWeight: '700',
                color: couleursCET.bleu,
                fontVariant: ['tabular-nums'],
            },
            quickStats: {
                flexDirection: 'row',
                flexWrap: 'wrap',
                rowGap: 14,
                marginTop: 16,
                marginHorizontal: 20,
                paddingTop: 16,
                paddingBottom: 18,
                borderTopWidth: 1,
                borderTopColor: couleursCET.grey200,
            },
            statItem: {
                width: isResponsive ? '50%' : '25%',
                alignItems: 'center',
            },
            statLabel: {
                fontSize: 12,
                fontWeight: '500',
                color: couleursCET.grey600,
            },
            statValue: {
                marginTop: 4,
                fontSize: 17,
                fontWeight: '700',
                color: couleursCET.grey800,
                fontVariant: ['tabular-nums'],
            },
            // Historique
            histoContainer: {
                borderTopWidth: 1,
                borderTopColor: couleursCET.grey200,
            },
            histoBouton: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                minHeight: 48,
                paddingHorizontal: 20,
            },
            histoTitre: {
                flex: 1,
                fontSize: 14,
                fontWeight: '600',
                color: couleursCET.grey800,
            },
            histoNb: {
                fontSize: 12,
                color: couleursCET.grey600,
            },
            histoListe: {
                paddingHorizontal: 20,
                paddingBottom: 16,
            },
            histoLigne: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
                paddingVertical: 11,
                borderTopWidth: 1,
                borderTopColor: couleursCET.grey200,
            },
            histoDate: {
                width: 80,
                fontSize: 13,
                color: couleursCET.grey600,
                fontVariant: ['tabular-nums'],
            },
            histoLibelleContainer: {
                flex: 1,
                minWidth: 0,
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
            },
            pastille: {
                width: 8,
                height: 8,
                borderRadius: 999,
            },
            histoLibelle: {
                fontSize: 14,
                fontWeight: '500',
                color: couleursCET.grey800,
            },
            tag: {
                height: 20,
                paddingHorizontal: 8,
                borderRadius: 999,
                justifyContent: 'center',
            },
            tagText: {
                fontSize: 11,
                fontWeight: '500',
            },
            histoValeur: {
                fontSize: 14,
                fontWeight: '700',
                fontVariant: ['tabular-nums'],
            },
            histoVide: {
                paddingVertical: 11,
                borderTopWidth: 1,
                borderTopColor: couleursCET.grey200,
                fontSize: 13,
                color: couleursCET.grey600,
            },
            // Bas de page
            subContent: {
                flexDirection: isResponsive ? 'column' : 'row',
                alignItems: 'flex-start',
                gap: 24,
            },
            column: {
                flex: isResponsive ? undefined : 1,
                width: isResponsive ? '100%' : undefined,
                minWidth: 0,
                gap: 10,
            },
            sectionTitre: {
                fontSize: 14,
                fontWeight: '600',
                color: couleursCET.grey800,
            },
            formCard: {
                backgroundColor: '#ffffff',
                borderWidth: 1,
                borderColor: couleursCET.grey200,
                borderRadius: 12,
                padding: 16,
                gap: 12,
                ...ombre(0.06, 3, 1),
            },
            champ: {
                gap: 6,
            },
            champLabel: {
                fontSize: 12,
                fontWeight: '500',
                color: couleursCET.grey600,
            },
            obligatoire: {
                color: couleursCET.danger,
            },
            dropdownWrapper: {
                position: 'relative',
                justifyContent: 'center',
            },
            dropdownPastille: {
                position: 'absolute',
                left: 14,
                width: 10,
                height: 10,
                borderRadius: 999,
                zIndex: 1,
            },
            dropdown: {
                borderWidth: 1.5,
                borderRadius: 4,
                paddingLeft: 34,
                paddingRight: 12,
                backgroundColor: '#ffffff',
                borderColor: couleursCET.grey400
            },
            modalItem: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
            },
            modalItemText: {
                fontSize: 16,
                color: '#374151',
            },
            bandeauNature: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                paddingVertical: 9,
                paddingHorizontal: 12,
                borderRadius: 8,
            },
            bandeauNatureText: {
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                fontWeight: '500',
                color: couleursCET.grey800,
            },
            saisieLigne: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
            },
            formInput: {
                flex: 1,
                minWidth: 0,
                height: 40,
                paddingHorizontal: 12,
                borderWidth: 1.5,
                borderRadius: 4,
                fontSize: 15,
                fontWeight: '600',
                color: couleursCET.grey800,
                backgroundColor: '#ffffff',
                fontVariant: ['tabular-nums'],
                borderColor: couleursCET.grey400
            },
            unite: {
                fontSize: 14,
                fontWeight: '500',
                color: couleursCET.grey600,
            },
            aide: {
                fontSize: 12,
                color: couleursCET.grey600,
            },
            aideErreur: {
                color: couleursCET.danger,
            },
            buttonAccept: {
                height: 40,
                borderRadius: 4,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: couleursCET.primaire,
            },
            buttonAcceptDisabled: {
                backgroundColor: couleursCET.primaireDesactive,
            },
            buttonText: {
                fontSize: 14,
                fontWeight: '600',
                color: couleursCET.texteBouton,
            },
            buttonTextDisabled: {
                color: '#ffffff',
            },
            confirmation: {
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 10,
                paddingVertical: 12,
                paddingHorizontal: 14,
                borderRadius: 8,
                backgroundColor: couleursCET.orangeBg,
            },
            confirmationText: {
                flex: 1,
                fontSize: 13,
                fontWeight: '500',
                color: couleursCET.orangeFg,
            },
            erreurEnvoi: {
                fontSize: 13,
                color: couleursCET.danger,
            },
            // Dotations épargnables
            dotationsContainer: {
                gap: 8,
            },
            dotation: {
                flexDirection: 'row',
                alignItems: 'stretch',
                borderRadius: 8,
                overflow: 'hidden',
                borderWidth: 1,
                borderColor: couleursCET.grey300,
                backgroundColor: '#ffffff',
            },
            dotationSelected: {
                borderWidth: 2,
                ...ombre(0.10, 14, 4),
            },
            dotationDimmed: {
                opacity: 0.5,
            },
            dotationBarre: {
                width: 5,
            },
            dotationContenu: {
                flex: 1,
                minWidth: 0,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                paddingVertical: 8,
                paddingHorizontal: 12,
            },
            dotationGauche: {
                flex: 1,
                minWidth: 0,
            },
            dotationLabel: {
                fontSize: 12,
                fontWeight: '500',
                color: couleursCET.grey600,
            },
            dotationValeur: {
                marginTop: 2,
                fontSize: 14,
                fontWeight: '500',
                color: couleursCET.grey800,
            },
            dotationValeurSelected: {
                fontWeight: '700',
            },
            rightText: {
                textAlign: 'right'
            },
            piedDotations: {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
            },
            conversion: {
                marginLeft: 'auto',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                paddingVertical: 6,
                paddingHorizontal: 12,
                borderWidth: 1,
                borderColor: couleursCET.grey200,
                borderRadius: 8,
                backgroundColor: couleursCET.grey50,
            },
            conversionText: {
                flexShrink: 1,
                fontSize: 12,
                color: couleursCET.grey600,
            },
            conversionGras: {
                fontWeight: '600',
                color: couleursCET.grey800,
            },
        })
    }
}
